import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Target, ArrowsClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { MatchRow } from "@/components/MatchCard";
import RequirementForm from "@/components/RequirementForm";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

function RequirementCard({ r }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showMatches, setShowMatches] = useState(false);
  const [editing, setEditing] = useState(false);

  const { data: matchData, isFetching, refetch } = useQuery({
    queryKey: ["req-matches", r.id],
    queryFn: async () => (await api.get(`/matching/requirement/${r.id}`)).data,
    enabled: showMatches,
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/requirements/${r.id}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["requirements"] });
      if (data?.pending_approval) toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      else toast.success(t("requirementDeleted", "Requirement deleted"));
    },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  const top = (matchData?.results || []).filter((m) => m.match.score >= 40).slice(0, 6);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`requirement-card-${r.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-sm bg-[#0B2545]/10 flex items-center justify-center flex-shrink-0"><Target size={18} className="text-[#0B2545]" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-slate-900 truncate">{r.title || t("matching.untitledRequirement", "Untitled requirement")}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${r.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
              {r.active ? t("matching.active", "Active") : t("matching.inactive", "Inactive")}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            <span className="uppercase tracking-wide">{t(`matching.party.${r.party_type}`, r.party_type)}</span>
            {r.contact_name && <span> · {r.contact_name}</span>}
          </div>
          {(r.budget_max || r.investment_size_max) && (
            <div className="text-xs text-slate-400 mt-1">{t("matching.budgetLabel", "Budget")}: {(r.budget_max || r.investment_size_max || 0).toLocaleString()} €</div>
          )}
        </div>
        <button onClick={() => setEditing(true)} data-testid={`edit-requirement-${r.id}`} className="text-slate-400 hover:text-[#0B2545]"><PencilSimple size={16} /></button>
        <AlertDialog>
          <AlertDialogTrigger asChild><button data-testid={`delete-requirement-${r.id}`} className="text-slate-400 hover:text-red-600"><Trash size={16} /></button></AlertDialogTrigger>
          <AlertDialogContent className="bg-white rounded-sm">
            <AlertDialogHeader><AlertDialogTitle>{t("matching.deleteRequirement", "Delete requirement?")}</AlertDialogTitle></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">{t("cancel", "Cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => remove.mutate()} className="rounded-sm bg-red-600 hover:bg-red-700">{t("delete", "Delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-sm" data-testid={`req-view-matches-${r.id}`} onClick={() => setShowMatches((v) => !v)}>
          {showMatches ? t("matching.hideMatches", "Hide matches") : t("matching.viewMatches", "View matches")}
        </Button>
        {showMatches && <Button variant="ghost" size="sm" className="rounded-sm text-[#C5A059]" onClick={() => refetch()}><ArrowsClockwise size={14} className="mr-1" /> {t("matching.recalculate", "Recalculate")}</Button>}
      </div>

      {showMatches && (
        <div className="mt-3 space-y-2" data-testid={`req-matches-${r.id}`}>
          {isFetching && !matchData ? (
            <div className="flex justify-center py-6"><div className="animate-spin h-6 w-6 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
          ) : top.length === 0 ? (
            <p className="text-slate-400 text-sm py-2">{t("matching.noMatches", "No matching properties yet.")}</p>
          ) : (
            top.map((m) => (
              <MatchRow key={m.property.id} name={m.property.title}
                subtitle={`${m.property.city || ""}${m.property.city ? " · " : ""}${m.property.category || ""}`}
                match={m.match} testid={`req-match-${r.id}-${m.property.id}`} />
            ))
          )}
        </div>
      )}
      {editing && <RequirementForm open={editing} onOpenChange={setEditing} initial={r} />}
    </div>
  );
}

export default function Requirements() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const { data: requirements = [], isLoading } = useQuery({
    queryKey: ["requirements"],
    queryFn: async () => (await api.get("/requirements")).data,
  });

  return (
    <div>
      <PageHeader
        title={t("navigation.requirements", "Requirements")}
        subtitle={t("matching.requirementsSubtitle", "Structured buyer, investor & developer briefs matched to your portfolio")}
        actions={
          <Button data-testid="add-requirement-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <Plus size={16} className="mr-1.5" /> {t("matching.addRequirement", "Add Requirement")}
          </Button>
        }
      />
      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : requirements.length === 0 ? (
        <EmptyState icon={Target} title={t("matching.noRequirements", "No requirements yet")} subtitle={t("matching.noRequirementsSub", "Create a buyer, investor or developer brief to find matching properties.")} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {requirements.map((r) => <RequirementCard key={r.id} r={r} />)}
        </div>
      )}
      {showForm && <RequirementForm open={showForm} onOpenChange={setShowForm} />}
    </div>
  );
}
